import React, { useState, useEffect } from 'react';
import { artikelPublicService } from '../../services/artikelPublicService';
import {
    FaCalendarAlt,
    FaUser,
    FaTag,
    FaArrowLeft,
    FaSearch,
    FaImage,
    FaSpinner,
    FaExclamationTriangle,
    FaFilePdf,
    FaDownload,
    FaEye,
    FaStar,
    FaMapPin,
    FaTimes,
    FaFolderOpen,
    // Ikon Share
    FaFacebookF,
    FaTwitter,
    FaWhatsapp,
    FaLinkedinIn,
    FaTelegramPlane,
    FaLink,
    FaCheck,
    FaShareAlt
} from 'react-icons/fa';

const ArtikelPage = () => {
    const [artikels, setArtikels] = useState([]);
    const [activeCategory, setActiveCategory] = useState('semua');
    const [selectedArticle, setSelectedArticle] = useState(null);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [copied, setCopied] = useState(false);

    // Kategori
    const categories = [
        { id: 'semua', title: 'Semua', icon: '📚' },
        { id: 'berita', title: 'Berita', icon: '📰' },
        { id: 'kegiatan', title: 'Kegiatan', icon: '🎯' },
        { id: 'artikel', title: 'Artikel', icon: '📝' },
        { id: 'pengumuman', title: 'Pengumuman', icon: '📢' },
    ];

    // Load artikels
    useEffect(() => {
        loadArtikels(1);
    }, [activeCategory, searchTerm]);

    const loadArtikels = async (page = 1) => {
        setLoading(true);
        setError('');

        const params = { page, per_page: 9 };
        if (activeCategory !== 'semua') params.kategori = activeCategory;
        if (searchTerm) params.search = searchTerm;

        const result = await artikelPublicService.getArtikels(params);

        if (result.success) {
            setArtikels(result.data.data || []);
            setTotalPages(result.data.last_page || 1);
            setCurrentPage(result.data.current_page || 1);
        } else {
            setError(result.message);
        }
        setLoading(false);
    };

    // Handle klik artikel - load detail
    const handleArticleClick = async (artikel) => {
        setLoadingDetail(true);
        const result = await artikelPublicService.getArtikelBySlug(artikel.slug);

        if (result.success) {
            setSelectedArticle(result.data.data || result.data);
            window.scrollTo(0, 0);
        } else {
            setError(result.message);
        }
        setLoadingDetail(false);
    };

    const handleCategoryChange = (catId) => {
        setActiveCategory(catId);
        setCurrentPage(1);
    };

    const handleBackToList = () => {
        setSelectedArticle(null);
        window.scrollTo(0, 0);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('id-ID', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    const getImageUrl = (path) => {
        if (!path) return '';
        if (path.startsWith('http')) return path;
        return path;
    };

    // ============ FUNGSI SHARE ============
    const getArticleUrl = () => {
        return window.location.href;
    };

    const handleShare = (platform) => {
        const url = encodeURIComponent(getArticleUrl());
        const title = encodeURIComponent(selectedArticle?.judul || 'Artikel BPKAD Kota Magelang');

        const shareUrls = {
            facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
            twitter: `https://twitter.com/intent/tweet?url=${url}&text=${title}`,
            whatsapp: `https://wa.me/?text=${title}%20${url}`,
            linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
            telegram: `https://t.me/share/url?url=${url}&text=${title}`,
        };

        if (platform === 'copy') {
            navigator.clipboard.writeText(getArticleUrl()).then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            }).catch((err) => {
                console.error('Gagal menyalin link:', err);
            });
            return;
        }

        window.open(shareUrls[platform], '_blank', 'noopener,noreferrer,width=600,height=500');
    };

    // ============ DETAIL VIEW ============
    if (selectedArticle) {
        return (
            <div className="container mx-auto px-4 py-12">
                <button
                    onClick={handleBackToList}
                    className="flex items-center text-[#2E7D32] hover:text-[#4CAF50] mb-6 font-medium transition-colors"
                >
                    <FaArrowLeft className="mr-2" /> Kembali ke Daftar Artikel
                </button>

                <article className="max-w-7xl mx-auto">
                    {/* Featured Image */}
                    {(selectedArticle.gambar || selectedArticle.thumbnail) && (
                        <img
                            src={getImageUrl(selectedArticle.gambar || selectedArticle.thumbnail)}
                            alt={selectedArticle.judul}
                            className="w-full h-64 md:h-96 object-cover rounded-xl mb-6 shadow-lg"
                            onError={(e) => {
                                e.target.style.display = 'none';
                            }}
                        />
                    )}

                    {/* Meta Info */}
                    <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-4">
                        <span className="flex items-center">
                            <FaCalendarAlt className="mr-1 text-[#2E7D32]" />
                            {formatDate(selectedArticle.published_at || selectedArticle.created_at)}
                        </span>
                        <span className="flex items-center">
                            <FaUser className="mr-1 text-[#2E7D32]" /> {selectedArticle.author}
                        </span>
                        <span className="flex items-center">
                            <FaEye className="mr-1 text-[#2E7D32]" /> {selectedArticle.views || 0} views
                        </span>
                        <span className="bg-[#2E7D32] text-white text-xs px-3 py-1 rounded-full capitalize">
                            {selectedArticle.kategori}
                        </span>
                    </div>

                    {/* Title */}
                    <h1 className="text-3xl md:text-4xl font-bold text-[#0F4C3A] mb-6">
                        {selectedArticle.judul}
                    </h1>

                    {/* Tags */}
                    {selectedArticle.tags && selectedArticle.tags.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-6">
                            {selectedArticle.tags.map((tag, index) => (
                                <span
                                    key={index}
                                    className="bg-[#2E7D32]/10 text-[#2E7D32] px-3 py-1 rounded-full text-sm"
                                >
                                    <FaTag className="inline mr-1" /> {tag}
                                </span>
                            ))}
                        </div>
                    )}

                    {/* ============ SHARE TO SOCIAL MEDIA ============ */}
                    <div className="flex flex-wrap items-center gap-3 py-4 mb-6 border-y border-gray-200">
                        <span className="flex items-center text-gray-600 font-semibold text-sm">
                            <FaShareAlt className="mr-2 text-[#2E7D32]" /> Bagikan:
                        </span>

                        {/* Facebook */}
                        <button
                            onClick={() => handleShare('facebook')}
                            aria-label="Bagikan ke Facebook"
                            title="Bagikan ke Facebook"
                            className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1877F2] text-white hover:scale-110 hover:shadow-lg transition-all duration-200"
                        >
                            <FaFacebookF />
                        </button>

                        {/* Twitter / X */}
                        <button
                            onClick={() => handleShare('twitter')}
                            aria-label="Bagikan ke Twitter"
                            title="Bagikan ke Twitter"
                            className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1DA1F2] text-white hover:scale-110 hover:shadow-lg transition-all duration-200"
                        >
                            <FaTwitter />
                        </button>

                        {/* WhatsApp */}
                        <button
                            onClick={() => handleShare('whatsapp')}
                            aria-label="Bagikan ke WhatsApp"
                            title="Bagikan ke WhatsApp"
                            className="w-10 h-10 flex items-center justify-center rounded-full bg-[#25D366] text-white hover:scale-110 hover:shadow-lg transition-all duration-200"
                        >
                            <FaWhatsapp />
                        </button>

                        {/* LinkedIn */}
                        <button
                            onClick={() => handleShare('linkedin')}
                            aria-label="Bagikan ke LinkedIn"
                            title="Bagikan ke LinkedIn"
                            className="w-10 h-10 flex items-center justify-center rounded-full bg-[#0A66C2] text-white hover:scale-110 hover:shadow-lg transition-all duration-200"
                        >
                            <FaLinkedinIn />
                        </button>

                        {/* Telegram */}
                        <button
                            onClick={() => handleShare('telegram')}
                            aria-label="Bagikan ke Telegram"
                            title="Bagikan ke Telegram"
                            className="w-10 h-10 flex items-center justify-center rounded-full bg-[#0088CC] text-white hover:scale-110 hover:shadow-lg transition-all duration-200"
                        >
                            <FaTelegramPlane />
                        </button>

                        {/* Copy Link */}
                        <button
                            onClick={() => handleShare('copy')}
                            aria-label="Salin link artikel"
                            title="Salin link"
                            className={`w-10 h-10 flex items-center justify-center rounded-full text-white hover:scale-110 hover:shadow-lg transition-all duration-200 ${copied ? 'bg-green-600' : 'bg-gray-600'
                                }`}
                        >
                            {copied ? <FaCheck /> : <FaLink />}
                        </button>

                        {/* Notifikasi "Tersalin" */}
                        {copied && (
                            <span className="text-sm text-green-600 font-medium animate-pulse">
                                Link berhasil disalin!
                            </span>
                        )}
                    </div>

                    {/* Content */}
                    <div className="prose max-w-none">
                        {selectedArticle.ringkasan && (
                            <p className="text-lg text-gray-700 leading-relaxed mb-6 italic border-l-4 border-[#2E7D32] pl-4">
                                {selectedArticle.ringkasan}
                            </p>
                        )}
                        <div
                            className="text-gray-700 leading-relaxed break-words 
                                        [&>p]:mb-4 [&>p]:leading-7 
                                        [&>h2]:mt-8 [&>h2]:mb-4 [&>h2]:text-2xl [&>h2]:font-bold
                                        [&>ul]:list-disc [&>ul]:ml-5 [&>ul]:mb-4
                                        prose-headings:text-[#0F4C3A] prose-a:text-[#2E7D32] prose-img:rounded-lg"
                            dangerouslySetInnerHTML={{ __html: selectedArticle.konten }}
                        />
                    </div>

                    {/* File Lampiran */}
                    {selectedArticle.file_lampiran && (
                        <div className="mt-8 p-4 bg-gray-50 rounded-lg border border-gray-200">
                            <h3 className="font-semibold text-[#0F4C3A] mb-3 flex items-center">
                                <FaFilePdf className="mr-2 text-red-500" />
                                File Lampiran
                            </h3>
                            <a
                                href={getImageUrl(selectedArticle.file_lampiran)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center justify-between p-3 bg-white rounded-lg hover:bg-gray-100 transition-colors border"
                            >
                                <span className="text-sm text-gray-700 truncate">
                                    {selectedArticle.file_name || 'Download File'}
                                </span>
                                <FaDownload className="text-[#2E7D32] flex-shrink-0" />
                            </a>
                        </div>
                    )}

                    {/* ============ SHARE BAWAH (Opsional) ============ */}
                    <div className="mt-10 pt-6 border-t border-gray-200">
                        <div className="flex flex-wrap items-center gap-3">
                            <span className="text-gray-600 font-semibold text-sm">
                                Suka artikel ini? Bagikan ke:
                            </span>
                            <button
                                onClick={() => handleShare('facebook')}
                                className="w-9 h-9 flex items-center justify-center rounded-full bg-[#1877F2] text-white hover:scale-110 transition-all"
                                title="Facebook"
                            >
                                <FaFacebookF size={14} />
                            </button>
                            <button
                                onClick={() => handleShare('twitter')}
                                className="w-9 h-9 flex items-center justify-center rounded-full bg-[#1DA1F2] text-white hover:scale-110 transition-all"
                                title="Twitter"
                            >
                                <FaTwitter size={14} />
                            </button>
                            <button
                                onClick={() => handleShare('whatsapp')}
                                className="w-9 h-9 flex items-center justify-center rounded-full bg-[#25D366] text-white hover:scale-110 transition-all"
                                title="WhatsApp"
                            >
                                <FaWhatsapp size={14} />
                            </button>
                            <button
                                onClick={() => handleShare('telegram')}
                                className="w-9 h-9 flex items-center justify-center rounded-full bg-[#0088CC] text-white hover:scale-110 transition-all"
                                title="Telegram"
                            >
                                <FaTelegramPlane size={14} />
                            </button>
                        </div>
                    </div>
                </article>
            </div>
        );
    }

    // ============ LIST VIEW ============
    return (
        <div className="container mx-auto px-4 py-12">
            {/* Header */}
            <div className="mb-8">
                <div className="flex items-center space-x-3 mb-4">
                    <FaImage className="text-4xl text-[#2E7D32]" />
                    <div>
                        <h1 className="text-3xl md:text-4xl font-bold text-[#0F4C3A]">
                            Artikel & Berita
                        </h1>
                        <p className="text-gray-600 mt-1">
                            Informasi kegiatan, berita, dan artikel seputar BPKAD Kota Magelang
                        </p>
                    </div>
                </div>
            </div>

            {/* Search Bar */}
            <div className="mb-8">
                <div className="relative">
                    <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Cari artikel berdasarkan judul atau ringkasan..."
                        className="w-full pl-12 pr-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent outline-none"
                    />
                    {searchTerm && (
                        <button
                            onClick={() => setSearchTerm('')}
                            className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                            <FaTimes />
                        </button>
                    )}
                </div>
            </div>

            {/* Kategori Navigation */}
            <div className="flex flex-wrap gap-3 mb-8">
                {categories.map((cat) => (
                    <button
                        key={cat.id}
                        onClick={() => handleCategoryChange(cat.id)}
                        className={`flex items-center px-5 py-2.5 rounded-lg font-semibold transition-all duration-300 ${activeCategory === cat.id
                            ? 'bg-[#0F4C3A] text-white shadow-lg scale-105'
                            : 'bg-white text-gray-600 hover:bg-gray-50 shadow'
                            }`}
                    >
                        <span className="mr-2 text-xl">{cat.icon}</span>
                        {cat.title}
                    </button>
                ))}
            </div>

            {/* Content */}
            {loading ? (
                <div className="text-center py-20">
                    <FaSpinner className="animate-spin text-4xl text-[#2E7D32] mx-auto mb-4" />
                    <p className="text-gray-500">Memuat artikel...</p>
                </div>
            ) : error ? (
                <div className="text-center py-20">
                    <FaExclamationTriangle className="text-5xl text-yellow-500 mx-auto mb-4" />
                    <p className="text-gray-500 mb-4">{error}</p>
                    <button
                        onClick={() => loadArtikels(1)}
                        className="btn-primary"
                    >
                        Coba Lagi
                    </button>
                </div>
            ) : artikels.length === 0 ? (
                <div className="text-center py-20">
                    <FaSearch className="text-6xl text-gray-300 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-gray-500 mb-2">Tidak Ditemukan</h3>
                    <p className="text-gray-400">
                        {searchTerm
                            ? 'Tidak ada artikel yang sesuai dengan pencarian'
                            : 'Belum ada artikel untuk kategori ini'}
                    </p>
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {artikels.map((article) => (
                            <div
                                key={article.id}
                                className="bg-white rounded-xl shadow-lg overflow-hidden cursor-pointer hover:shadow-xl transition-all duration-300 group"
                                onClick={() => handleArticleClick(article)}
                            >
                                {/* Image */}
                                <div className="relative overflow-hidden h-48">
                                    {article.thumbnail || article.gambar ? (
                                        <img
                                            src={getImageUrl(article.thumbnail || article.gambar)}
                                            alt={article.judul}
                                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                            onError={(e) => {
                                                e.target.src = 'https://placehold.co/400x300/0F4C3A/FFFFFF?text=Artikel';
                                            }}
                                        />
                                    ) : (
                                        <div className="w-full h-full bg-gradient-to-br from-[#0F4C3A] to-[#2E7D32] flex items-center justify-center">
                                            <FaImage className="text-5xl text-white/50" />
                                        </div>
                                    )}

                                    {/* Badges */}
                                    <div className="absolute top-2 left-2 flex gap-1">
                                        {article.is_featured && (
                                            <span className="bg-yellow-500 text-white text-xs px-2 py-1 rounded-full flex items-center shadow">
                                                <FaStar className="mr-1" /> Featured
                                            </span>
                                        )}
                                        {article.is_pinned && (
                                            <span className="bg-blue-500 text-white text-xs px-2 py-1 rounded-full flex items-center shadow">
                                                <FaMapPin className="mr-1" /> Pinned
                                            </span>
                                        )}
                                    </div>

                                    {/* Hover Overlay */}
                                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                                        <span className="text-white font-semibold bg-[#2E7D32] px-4 py-2 rounded-lg">
                                            Baca Selengkapnya
                                        </span>
                                    </div>
                                </div>

                                {/* Content */}
                                <div className="p-6">
                                    <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                                        <span className="flex items-center">
                                            <FaCalendarAlt className="mr-1" />
                                            {formatDate(article.published_at || article.created_at)}
                                        </span>
                                        <span className="flex items-center">
                                            <FaUser className="mr-1" /> {article.author}
                                        </span>
                                    </div>

                                    <span className="inline-block bg-[#2E7D32]/10 text-[#2E7D32] text-xs px-2 py-1 rounded-full mb-2 capitalize">
                                        {article.kategori}
                                    </span>

                                    <h3 className="text-lg font-bold text-[#0F4C3A] mb-3 group-hover:text-[#2E7D32] transition-colors line-clamp-2">
                                        {article.judul}
                                    </h3>

                                    <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                                        {article.ringkasan ||
                                            article.konten?.replace(/<[^>]*>/g, '').substring(0, 150) + '...'}
                                    </p>

                                    {article.tags && article.tags.length > 0 && (
                                        <div className="flex flex-wrap gap-2">
                                            {article.tags.slice(0, 3).map((tag, index) => (
                                                <span
                                                    key={index}
                                                    className="bg-gray-100 text-gray-600 px-2 py-1 rounded-full text-xs"
                                                >
                                                    #{tag}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="flex justify-center items-center gap-2 mt-10">
                            <button
                                onClick={() => loadArtikels(currentPage - 1)}
                                disabled={currentPage === 1}
                                className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50 transition-colors"
                            >
                                Sebelumnya
                            </button>
                            <span className="px-4 py-2 text-gray-600">
                                Halaman {currentPage} dari {totalPages}
                            </span>
                            <button
                                onClick={() => loadArtikels(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50 transition-colors"
                            >
                                Selanjutnya
                            </button>
                        </div>
                    )}
                </>
            )}

            {/* Loading Detail Overlay */}
            {loadingDetail && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
                    <div className="bg-white rounded-xl p-6 text-center shadow-2xl">
                        <FaSpinner className="animate-spin text-3xl text-[#2E7D32] mx-auto mb-2" />
                        <p className="text-gray-500">Memuat artikel...</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ArtikelPage;